// Peak Periods Editor Component
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Slider } from '@/components/ui/slider';
import { Badge } from '@/components/ui/badge';
import { PeakPeriod } from '@/types/epse';
import { Plus, Trash2, Zap, Clock } from 'lucide-react';
import { useMemo } from 'react';
import { Area, AreaChart, ReferenceLine, ResponsiveContainer, XAxis, YAxis } from 'recharts';

interface EPSEPeakPeriodsEditorProps {
  periods: PeakPeriod[];
  onChange: (periods: PeakPeriod[]) => void;
  baseLoadKw: number;
  peakKw: number;
}

export function EPSEPeakPeriodsEditor({ periods, onChange, baseLoadKw, peakKw }: EPSEPeakPeriodsEditorProps) {
  const addPeriod = () => {
    const newPeriod: PeakPeriod = {
      id: crypto.randomUUID(),
      start_hour: 9,
      end_hour: 11,
      intensity: 1.0,
      label: `Piek ${periods.length + 1}`,
    };
    onChange([...periods, newPeriod]);
  };

  const updatePeriod = (id: string, updates: Partial<PeakPeriod>) => {
    onChange(periods.map(p => p.id === id ? { ...p, ...updates } : p));
  };

  const removePeriod = (id: string) => {
    onChange(periods.filter(p => p.id !== id));
  };

  const formatTime = (hour: number) => {
    const h = Math.floor(hour);
    const m = Math.round((hour - h) * 60);
    return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
  };

  // Generate preview chart data
  const previewData = useMemo(() => {
    const data: { time: string; kw: number; hour: number }[] = [];
    
    for (let q = 0; q < 96; q++) {
      const hour = q / 4;
      const h = Math.floor(hour);
      const m = (q % 4) * 15;
      
      let kw = baseLoadKw;
      
      // Apply each peak period
      for (const peak of periods) {
        if (hour >= peak.start_hour && hour <= peak.end_hour) {
          const midpoint = (peak.start_hour + peak.end_hour) / 2;
          const width = (peak.end_hour - peak.start_hour) / 2;
          const gaussian = Math.exp(-Math.pow((hour - midpoint) / Math.max(width, 0.5), 2) * 2);
          kw += (peakKw - baseLoadKw) * gaussian * peak.intensity;
        }
      }
      
      data.push({
        time: `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`,
        kw: Math.round(kw * 10) / 10,
        hour,
      });
    }
    
    return data;
  }, [periods, baseLoadKw, peakKw]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Zap className="h-5 w-5" />
            Piekmomenten
          </span>
          <Button variant="outline" size="sm" onClick={addPeriod}>
            <Plus className="h-4 w-4 mr-1" />
            Toevoegen
          </Button>
        </CardTitle>
        <CardDescription>
          Definieer meerdere piekperiodes per dag met verschillende intensiteiten
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Peak periods list */}
        <div className="space-y-4">
          {periods.length === 0 ? (
            <p className="text-sm text-muted-foreground text-center py-4">
              Geen piekmomenten gedefinieerd. Voeg er een toe om te beginnen.
            </p>
          ) : (
            periods.map((period, index) => (
              <div key={period.id} className="border rounded-lg p-4 space-y-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <Badge variant="outline">#{index + 1}</Badge>
                    <Input
                      value={period.label || ''}
                      onChange={(e) => updatePeriod(period.id, { label: e.target.value })}
                      className="w-32 h-7 text-sm"
                      placeholder="Label"
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8 text-destructive hover:text-destructive"
                    onClick={() => removePeriod(period.id)}
                  >
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
                
                {/* Time range */}
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <Label className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Start
                      </Label>
                      <span className="font-medium">{formatTime(period.start_hour)}</span>
                    </div>
                    <Slider
                      value={[period.start_hour]}
                      onValueChange={([v]) => updatePeriod(period.id, { 
                        start_hour: v,
                        end_hour: Math.max(v + 0.5, period.end_hour)
                      })}
                      min={6}
                      max={21}
                      step={0.25}
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <Label className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        Einde
                      </Label>
                      <span className="font-medium">{formatTime(period.end_hour)}</span>
                    </div>
                    <Slider
                      value={[period.end_hour]}
                      onValueChange={([v]) => updatePeriod(period.id, { 
                        end_hour: v,
                        start_hour: Math.min(v - 0.5, period.start_hour)
                      })}
                      min={6.5}
                      max={22}
                      step={0.25}
                    />
                  </div>
                </div>
                
                {/* Intensity */}
                <div className="space-y-2">
                  <div className="flex items-center justify-between text-sm">
                    <Label>Intensiteit</Label>
                    <Badge variant={period.intensity > 1 ? 'default' : period.intensity < 1 ? 'secondary' : 'outline'}>
                      {(period.intensity * 100).toFixed(0)}%
                    </Badge>
                  </div>
                  <Slider
                    value={[period.intensity * 100]}
                    onValueChange={([v]) => updatePeriod(period.id, { intensity: v / 100 })}
                    min={50}
                    max={150}
                    step={5}
                  />
                  <p className="text-xs text-muted-foreground">
                    50% = halve piek | 100% = normale piek | 150% = extra hoge piek
                  </p>
                </div>
              </div>
            ))
          )}
        </div>
        
        {/* Preview chart */}
        {periods.length > 0 && (
          <div className="pt-4 border-t">
            <Label className="mb-2 block">Dagprofiel Preview</Label>
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={previewData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                  <XAxis 
                    dataKey="time" 
                    tick={{ fontSize: 10 }} 
                    tickFormatter={(v) => v.split(':')[0]}
                    interval={11}
                  />
                  <YAxis 
                    tick={{ fontSize: 10 }} 
                    width={40}
                    tickFormatter={(v) => `${v}`}
                  />
                  <Area
                    type="monotone"
                    dataKey="kw"
                    stroke="hsl(var(--primary))"
                    fill="hsl(var(--primary) / 0.2)"
                    strokeWidth={2}
                  />
                  <ReferenceLine
                    y={baseLoadKw}
                    stroke="hsl(var(--muted-foreground))"
                    strokeDasharray="4 4"
                    strokeWidth={1}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex gap-4 mt-2 text-xs text-muted-foreground">
              <span>— Basisbelasting: {baseLoadKw.toFixed(0)} kW</span>
              <span>— Piekvermogen: {peakKw.toFixed(0)} kW</span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
